import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../application/account/AuthProvider';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { useAppSettings } from '../../application/pwa/useAppSettings';
import { BellIcon, CloseIcon, InstallIcon, PersonIcon } from '../icons/ControlIcons';
import { ChevronDownIcon } from './BrandIcons';
import AppDrawerStatus from './AppDrawerStatus';

/**
 * The settings drawer (SPEC-018).
 *
 * A real `<dialog>` opened with `showModal()`, exactly as `FilterSheet` is and
 * for the reason SPEC-006 D-3 gives: focus trapping, Escape, the top layer and
 * an inert background are four things the platform gets right and a hand-rolled
 * sheet gets subtly wrong.
 *
 * Deliberately the OPPOSITE choice from the offer cards in SPEC-016, which are
 * plain sections in the page flow. That difference is the whole argument: an
 * offer arrives uninvited and must never block, and this is a surface the
 * visitor went looking for. Blocking is what a menu is FOR.
 */
/**
 * The account, first among the sections: it is the one setting that changes
 * what the app IS — a garden on this device, or a garden that follows you
 * (SPEC-022). Sign-in is Google or an emailed link, the same two doors as
 * habb.me, because the two products share their accounts (ADR-0003).
 */
/**
 * One setting, as a disclosure (SPEC-018 D-10).
 *
 * The summary carries the two things the panel exists to show — what the
 * setting IS and what state it is in — and the body carries everything you
 * would only want once you had decided to act on it. Four sections' worth of
 * hints, buttons and a sign-in form came to 1006px in a 457px window; four
 * summaries come to about a quarter of that, and nothing scrolls.
 *
 * A real `<details>`, so the open state, the keyboard, the screen reader and
 * find-in-page are the platform's job rather than a `useState` and an
 * `aria-expanded` somebody has to remember.
 *
 * `data-setting` is how the tests address a section. `.app-setting__state` is
 * shared by all four, so anything positional — `.first()` — silently reads the
 * wrong one the moment a section is added above it, which is exactly what
 * happened when the account section arrived.
 */
function SettingSection({ name, glyph, label, state, children }) {
    return (
        <details className="app-setting" data-setting={name}>
            <summary className="app-setting__summary">
                <span className="app-setting__glyph" aria-hidden="true">{glyph}</span>
                <h3 className="app-setting__label">{label}</h3>
                <p className="app-setting__state">{state}</p>
                <ChevronDownIcon className="app-setting__caret" size={16} />
            </summary>

            <div className="app-setting__detail">{children}</div>
        </details>
    );
}

function AccountSetting() {
    const { t } = useLocale();
    const { user, authStatus, isAuthenticated, signIn, signOut, sendEmailLink, emailLinkState } = useAuth();
    const [emailAddress, setEmailAddress] = useState('');

    // Signed in, the state line is the person, not a status word.
    const stateKey = authStatus === 'loading' ? 'settings.account.state.checking' : 'settings.account.state.signedOut';

    const emailFeedbackKey = {
        sending: 'settings.account.email.state.sending',
        sent: 'settings.account.email.state.sent',
        completing: 'settings.account.email.state.completing',
        error: emailLinkState.errorKey,
    }[emailLinkState.status] || null;

    return (
        <SettingSection
            name="account"
            glyph={<PersonIcon size={22} />}
            label={t('settings.account.label')}
            state={isAuthenticated ? (user.name || user.email) : t(stateKey)}
        >
            <p className="app-setting__hint">
                {t(isAuthenticated ? 'settings.account.hint.signedIn' : 'settings.account.hint.signedOut')}
            </p>

            {isAuthenticated ? (
                <button
                    type="button"
                    className="button button--quiet app-setting__action"
                    onClick={signOut}
                    disabled={authStatus === 'loading'}
                >
                    {t('settings.account.action.signOut')}
                </button>
            ) : (
                <>
                    <button
                        type="button"
                        className="button button--primary app-setting__action"
                        onClick={signIn}
                        disabled={authStatus === 'loading'}
                    >
                        {t('settings.account.action.google')}
                    </button>

                    <form
                        className="app-setting__form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            sendEmailLink(emailAddress);
                        }}
                    >
                        <label className="app-setting__input-label" htmlFor="account-email">
                            {t('settings.account.email.label')}
                        </label>
                        <div className="app-setting__form-row">
                            <input
                                id="account-email"
                                className="app-setting__input"
                                type="email"
                                autoComplete="email"
                                value={emailAddress}
                                onChange={(event) => setEmailAddress(event.target.value)}
                            />
                            <button
                                type="submit"
                                className="button button--quiet"
                                disabled={emailLinkState.status === 'sending'}
                            >
                                {t('settings.account.email.action')}
                            </button>
                        </div>
                        {/* The one line that answers "did that work?" — sent,
                            sending, finishing, or why not. */}
                        <p className="app-setting__hint" role="status">
                            {emailFeedbackKey ? t(emailFeedbackKey) : ''}
                        </p>
                    </form>
                </>
            )}
        </SettingSection>
    );
}

export default function AppDrawer({ onClose }) {
    const { t, localeCode, languageCode } = useLocale();
    const dialogRef = useRef(null);
    const {
        notification,
        install,
        isBusy,
        statusRows,
        turnOnNotifications,
        setMuted,
        installApp,
    } = useAppSettings({ isOpen: true, localeCode, languageCode });

    // Opening is a one-off, and deliberately not in the same effect as the
    // listener below — the same split `FilterSheet` documents: this component
    // re-renders on every toggle, and an effect depending on the handler would
    // close and re-open the dialog each time, throwing on the second showModal.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    /**
     * Tell the parent it may unmount us — but not until the sheet has finished
     * leaving (SPEC-018 D-8).
     *
     * `AppHeader` renders this component only while the drawer is open, which
     * is what makes `useAppSettings` re-read on every open. It also means that
     * calling `onClose` the moment `close` fires deletes the element mid-exit,
     * so the close is announced only once the animations it started settle.
     *
     * **Bound ONCE, and the callback reached through a ref.** `AppHeader` passes
     * `onClose={() => setIsOpen(false)}` — a new function on every render — so
     * an effect that depended on it re-ran whenever anything above re-rendered.
     * Its cleanup cancelled the in-flight close, and the drawer simply stayed
     * open: press the close button, watch it slide down, watch it stay. Rare
     * until SPEC-022 put an account section inside the sheet and re-renders
     * during the 260ms exit became ordinary. The ref keeps the newest callback
     * without ever making the subscription depend on its identity.
     *
     * The no-animation path stays SYNCHRONOUS. Engines without `getAnimations`
     * — and jsdom, which has no layout at all — fall straight through, so
     * nothing here depends on a frame a test environment may never paint.
     */
    const onCloseRef = useRef(onClose);

    onCloseRef.current = onClose;

    useEffect(() => {
        const dialog = dialogRef.current;

        const handleClose = () => {
            // Forces the style recalculation that CREATES the exit transition.
            // Without it `getAnimations` can be asked before the browser has
            // resolved the closed state, and answer with an empty list.
            void dialog.offsetHeight;

            const animations = typeof dialog.getAnimations === 'function'
                ? dialog.getAnimations({ subtree: true })
                : [];

            if (animations.length === 0) {
                onCloseRef.current();
                return;
            }

            Promise.allSettled(animations.map((animation) => animation.finished))
                .then(() => onCloseRef.current());
        };

        dialog.addEventListener('close', handleClose);

        return () => dialog.removeEventListener('close', handleClose);
    }, []);

    return (
        <dialog
            className="app-drawer"
            ref={dialogRef}
            aria-labelledby="app-drawer-title"
            onClick={(event) => {
                if (event.target === dialogRef.current) {
                    dialogRef.current.close();
                }
            }}
        >
            {/* Decorative. The drawer is dismissed by the close button, by
                Escape or by a tap outside, and a bar that only looks draggable
                must not claim a role. */}
            <span className="app-drawer__grip" aria-hidden="true" />

            <div className="app-drawer__head">
                <h2 className="app-drawer__title" id="app-drawer-title">{t('settings.title')}</h2>
                <button
                    type="button"
                    className="app-drawer__close"
                    onClick={() => dialogRef.current.close()}
                    aria-label={t('settings.close')}
                >
                    <CloseIcon size={20} />
                </button>
            </div>

            <div className="app-drawer__body">
                <AccountSetting />

                <SettingSection
                    name="notify"
                    glyph={<BellIcon size={22} />}
                    label={t('settings.notify.label')}
                    state={t(notification.stateKey)}
                >
                    <p className="app-setting__hint">{t(notification.hintKey)}</p>

                    {notification.canTurnOn ? (
                        <button
                            type="button"
                            className="button button--primary app-setting__action"
                            onClick={turnOnNotifications}
                            disabled={isBusy}
                        >
                            {t('settings.notify.action.turnOn')}
                        </button>
                    ) : null}

                    {/* A switch, not a button, because it has exactly two states
                        and both are visible. It never claims to revoke
                        permission: the label says this app stops sending, which
                        is the only half a page owns (SPEC-018 D-2). */}
                    {notification.canPause || notification.canResume ? (
                        <label className="app-switch">
                            <input
                                type="checkbox"
                                className="app-switch__input"
                                role="switch"
                                checked={notification.canPause}
                                onChange={(event) => setMuted(!event.target.checked)}
                            />
                            <span className="app-switch__track" aria-hidden="true" />
                            <span className="app-switch__label">{t('settings.notify.action.send')}</span>
                        </label>
                    ) : null}
                </SettingSection>

                <SettingSection
                    name="install"
                    glyph={<InstallIcon size={22} />}
                    label={t('settings.install.label')}
                    state={t(install.stateKey)}
                >
                    {install.howToKey ? <p className="app-setting__hint">{t(install.howToKey)}</p> : null}

                    {install.canInstall ? (
                        <button
                            type="button"
                            className="button button--primary app-setting__action"
                            onClick={installApp}
                        >
                            {t('settings.install.action')}
                        </button>
                    ) : null}
                </SettingSection>

                <AppDrawerStatus rows={statusRows} />
            </div>
        </dialog>
    );
}
